import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card, PageHeader, Button, Input, Table, Th, Td } from "../components/ui";
import { errMsg } from "../lib/utils";
import { toast } from "sonner";
import { Save, ShieldAlert, ToggleLeft, ToggleRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useFeatureFlags } from "../lib/featureFlags";

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "administrator";
  const { refresh: refreshFlags } = useFeatureFlags();
  const [rows, setRows] = useState([]);
  const [edited, setEdited] = useState({});
  const [flags, setFlags] = useState([]);

  const load = () => api.get("/settings").then((r) => setRows(r.data));
  const loadFlags = () => api.get("/feature-flags").then((r) => setFlags(r.data));
  useEffect(() => { load(); loadFlags(); }, []);

  const save = async (key_name) => {
    try {
      const val = edited[key_name] ?? rows.find((r) => r.key_name === key_name)?.key_value;
      await api.put(`/settings/${key_name}`, { key_name, key_value: String(val) });
      toast.success("Tersimpan");
      setEdited((e) => { const n = { ...e }; delete n[key_name]; return n; });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  // Simpan semua field kop_* yang di-edit sekaligus
  const saveAllKop = async () => {
    const kopKeys = ["kop_address", "kop_phone", "kop_email", "kop_website", "kop_tagline"];
    const toSave = kopKeys.filter((k) => k in edited);
    if (toSave.length === 0) { toast.info("Tidak ada perubahan"); return; }
    try {
      await Promise.all(toSave.map((k) =>
        api.put(`/settings/${k}`, { key_name: k, key_value: String(edited[k]) })
      ));
      toast.success(`${toSave.length} field kop surat tersimpan`);
      setEdited((e) => { const n = { ...e }; toSave.forEach((k) => delete n[k]); return n; });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleFlag = async (key, enabled) => {
    try {
      await api.put(`/feature-flags/${key}`, { enabled });
      toast.success(`Modul ${key} ${enabled ? "diaktifkan" : "dinonaktifkan"}`);
      loadFlags();
      refreshFlags();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const tax = rows.filter((r) => r.key_name.startsWith("tax_ppn"));
  const company = rows.filter((r) => r.key_name.startsWith("company_"));
  const kop = rows.filter((r) => r.key_name.startsWith("kop_"));
  const issuerKeys = rows.filter((r) => (r.key_name.startsWith("signer_") || r.key_name.startsWith("address_") || r.key_name.startsWith("npwp_") || r.key_name.startsWith("bank_")));
  const logoRow = rows.find((r) => r.key_name === "invoice_logo_b64");

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Logo > 2MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        await api.post("/settings/invoice-logo", { image_b64: reader.result });
        toast.success("Logo tersimpan");
        load();
      } catch (e2) { toast.error(errMsg(e2)); }
    };
    reader.readAsDataURL(file);
  };

  const labelMap = {
    company_name: "Nama Perusahaan (Default)", company_npwp: "NPWP Perusahaan (Default)", company_address: "Alamat Perusahaan (Default)",
    company_signer: "Penandatangan (Default/Global)", company_bank_name: "Bank Name (Default/Global)", company_bank_holder: "Bank A/N (Default/Global)", company_bank_account: "Bank A/C (Default/Global)",
    tax_ppn_0: "PPN 0%", tax_ppn_1: "PPN 1.1%", tax_ppn_11: "PPN 11%",
    kop_address: "Alamat Kop Surat",
    kop_phone: "Telepon Kop Surat",
    kop_email: "Email Kop Surat",
    kop_website: "Website Kop Surat",
    kop_tagline: "Tagline Kop Surat",
  };
  const kopPlaceholder = {
    kop_address: "Grand Galaxy City RSK 6 No. 73 Jakasetia, Bekasi Selatan, Bekasi 17147",
    kop_phone: "021-1234567 / 0812-3456-7890",
    kop_email: "info@ar-cargo.com",
    kop_website: "http://ar-cargo.com",
    kop_tagline: "Solusi Pengiriman Tercepat, Terpercaya dan Profesional",
  };
  const issuerLabel = (key) => {
    if (key.startsWith("signer_")) return `Penandatangan — ${key.slice(7)}`;
    if (key.startsWith("address_")) return `Alamat — ${key.slice(8)}`;
    if (key.startsWith("npwp_")) return `NPWP — ${key.slice(5)}`;
    if (key.startsWith("bank_name_")) return `Bank Name — ${key.slice(10)}`;
    if (key.startsWith("bank_holder_")) return `Bank A/N — ${key.slice(12)}`;
    if (key.startsWith("bank_account_")) return `Bank A/C — ${key.slice(13)}`;
    return key;
  };

  const renderRow = (r) => (
    <tr key={r.key_name}>
      <Td className="font-mono text-xs">{r.key_name}</Td>
      <Td>{labelMap[r.key_name] || r.key_name}</Td>
      <Td>
        <Input data-testid={`setting-${r.key_name}`} value={edited[r.key_name] ?? r.key_value} onChange={(e) => setEdited({ ...edited, [r.key_name]: e.target.value })}/>
      </Td>
      <Td className="text-right">
        <Button variant="secondary" onClick={() => save(r.key_name)} disabled={!(r.key_name in edited)}><Save size={14}/> Simpan</Button>
      </Td>
    </tr>
  );

  return (
    <div data-testid="settings-page">
      <PageHeader title="Pengaturan" subtitle="Kelola tarif pajak, profil perusahaan, dan modul aktif." />

      <Card className="mb-5">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <h3 className="font-bold">Aktivasi Modul</h3>
          {!isAdmin && <span className="text-xs text-amber-700 flex items-center gap-1"><ShieldAlert size={12}/> hanya admin</span>}
        </div>
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-xs text-amber-900">
          <strong>Penting:</strong> Mengaktifkan modul di sini hanya membuka modul secara global. Akses per-user tetap diatur di <Link to="/users" className="font-bold underline">User Management</Link> — admin harus mencentang permission modul untuk setiap user.
        </div>
        <div className="divide-y divide-slate-200">
          {flags.map((f) => (
            <div key={f.key} data-testid={`feature-flag-${f.key}`} className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0 mr-4">
                <div className="font-bold text-sm">{f.label}</div>
                <div className="text-xs text-slate-500">{f.description}</div>
                <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mt-0.5">{f.key}</div>
              </div>
              <button
                data-testid={`feature-toggle-${f.key}`}
                disabled={!isAdmin}
                onClick={() => toggleFlag(f.key, !f.enabled)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${f.enabled ? "bg-emerald-600" : "bg-slate-300"}`}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${f.enabled ? "translate-x-6" : "translate-x-1"}`}/>
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card className="mb-5">
        <div className="px-4 py-3 border-b border-slate-200"><h3 className="font-bold">Pajak (PPN)</h3></div>
        <Table>
          <thead><tr><Th>Key</Th><Th>Label</Th><Th>Nilai (%)</Th><Th></Th></tr></thead>
          <tbody>{tax.map(renderRow)}</tbody>
        </Table>
      </Card>

      <Card className="mb-5">
        <div className="px-4 py-3 border-b border-slate-200"><h3 className="font-bold">Profil Perusahaan (Default)</h3></div>
        <Table>
          <thead><tr><Th>Key</Th><Th>Label</Th><Th>Nilai</Th><Th></Th></tr></thead>
          <tbody>{company.map(renderRow)}</tbody>
        </Table>
      </Card>

      <Card className="mb-5">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-bold">Logo Invoice</h3>
          <p className="text-xs text-slate-500 mt-1">Logo dipakai di Excel invoice template. Max 2MB, format PNG/JPG.</p>
        </div>
        <div className="p-4 flex items-start gap-6">
          <div className="w-48 h-32 bg-slate-50 border-2 border-dashed border-slate-300 rounded flex items-center justify-center overflow-hidden" data-testid="logo-preview">
            {logoRow?.key_value ? (
              <img src={logoRow.key_value.startsWith("data:") ? logoRow.key_value : `data:image/png;base64,${logoRow.key_value}`} alt="Logo" className="max-w-full max-h-full"/>
            ) : (
              <span className="text-xs text-slate-400">Belum ada logo</span>
            )}
          </div>
          <div className="flex-1">
            <label className="inline-block">
              <input data-testid="logo-upload-input" type="file" accept="image/png,image/jpeg,image/webp" onChange={uploadLogo} className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded file:border-0 file:bg-blue-600 file:text-white file:font-bold file:cursor-pointer hover:file:bg-blue-700"/>
            </label>
            <p className="text-[11px] text-slate-500 mt-2">Logo akan muncul di pojok kiri atas invoice Excel di atas banner &quot;INVOICE&quot;.</p>
          </div>
        </div>
      </Card>

      {/* === KOP SURAT — dipakai untuk semua invoice === */}
      <Card className="mb-5" data-testid="kop-surat-section">
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-bold inline-flex items-center gap-2">📄 Kop Surat Template Invoice</h3>
          <p className="text-xs text-slate-500 mt-1">
            Info kop surat yang akan tampil di <strong>header semua invoice</strong> (semua center-aligned di bawah logo).
            Nama perusahaan otomatis diambil dari <em>Perusahaan Issuer</em> yang dipilih saat Input AWB.
          </p>
        </div>
        {/* Preview live */}
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">Preview Kop</div>
          <div className="border border-slate-300 bg-white rounded p-4 text-center" data-testid="kop-preview">
            {logoRow?.key_value && (
              <img
                src={logoRow.key_value.startsWith("data:") ? logoRow.key_value : `data:image/png;base64,${logoRow.key_value}`}
                alt="Logo"
                className="mx-auto mb-2"
                style={{ maxHeight: 60 }}
              />
            )}
            <div className="text-base font-bold text-blue-900 uppercase">— NAMA PERUSAHAAN (dari AWB) —</div>
            <div className="text-xs text-slate-600 mt-1">{(edited.kop_address ?? kop.find(k=>k.key_name==='kop_address')?.key_value) || <em className="text-slate-400">(alamat kosong)</em>}</div>
            {((edited.kop_phone ?? kop.find(k=>k.key_name==='kop_phone')?.key_value) || (edited.kop_email ?? kop.find(k=>k.key_name==='kop_email')?.key_value)) && (
              <div className="text-xs text-slate-600 mt-0.5">
                {(edited.kop_phone ?? kop.find(k=>k.key_name==='kop_phone')?.key_value) && `Telp: ${edited.kop_phone ?? kop.find(k=>k.key_name==='kop_phone')?.key_value}`}
                {(edited.kop_phone ?? kop.find(k=>k.key_name==='kop_phone')?.key_value) && (edited.kop_email ?? kop.find(k=>k.key_name==='kop_email')?.key_value) && " · "}
                {(edited.kop_email ?? kop.find(k=>k.key_name==='kop_email')?.key_value) && `Email: ${edited.kop_email ?? kop.find(k=>k.key_name==='kop_email')?.key_value}`}
              </div>
            )}
            {(edited.kop_website ?? kop.find(k=>k.key_name==='kop_website')?.key_value) && (
              <div className="text-xs text-slate-600 mt-0.5">Website: {edited.kop_website ?? kop.find(k=>k.key_name==='kop_website')?.key_value}</div>
            )}
            {(edited.kop_tagline ?? kop.find(k=>k.key_name==='kop_tagline')?.key_value) && (
              <div className="text-xs italic text-blue-900 mt-1">{edited.kop_tagline ?? kop.find(k=>k.key_name==='kop_tagline')?.key_value}</div>
            )}
          </div>
        </div>
        {/* Form input */}
        <Table>
          <thead><tr><Th>Field</Th><Th>Nilai</Th></tr></thead>
          <tbody>
            {["kop_address", "kop_phone", "kop_email", "kop_website", "kop_tagline"].map((k) => {
              const r = kop.find((x) => x.key_name === k);
              if (!r) return null;
              return (
                <tr key={k}>
                  <Td className="w-52">{labelMap[k]}</Td>
                  <Td>
                    <Input
                      data-testid={`setting-${k}`}
                      value={edited[k] ?? r.key_value}
                      onChange={(e) => setEdited({ ...edited, [k]: e.target.value })}
                      placeholder={kopPlaceholder[k] || ""}
                    />
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        <div className="p-4 flex items-center justify-between border-t border-slate-200 bg-slate-50">
          <p className="text-[11px] text-slate-500">
            Edit di atas, lalu klik <strong>Simpan Semua</strong>.
            Perubahan langsung berlaku ke invoice yang di-export berikutnya.
          </p>
          <Button
            data-testid="kop-save-all-btn"
            onClick={saveAllKop}
            disabled={!kop.some((r) => r.key_name in edited)}
          >
            <Save size={14}/> Simpan Semua Kop Surat
          </Button>
        </div>
      </Card>

      <Card>
        <div className="px-4 py-3 border-b border-slate-200">
          <h3 className="font-bold">Per Issuer — Signer / Alamat / NPWP / Bank</h3>
          <p className="text-xs text-slate-500 mt-1">Setting per Perusahaan Issuer (PT. Abirama Mitra Pradana / Pratama) untuk dipakai di Invoice Excel.</p>
        </div>
        <Table>
          <thead><tr><Th>Key</Th><Th>Label</Th><Th>Nilai</Th><Th></Th></tr></thead>
          <tbody>
            {issuerKeys.map((r) => (
              <tr key={r.key_name}>
                <Td className="font-mono text-[10px]">{r.key_name}</Td>
                <Td>{issuerLabel(r.key_name)}</Td>
                <Td>
                  <Input data-testid={`setting-${r.key_name}`} value={edited[r.key_name] ?? r.key_value} onChange={(e) => setEdited({ ...edited, [r.key_name]: e.target.value })}/>
                </Td>
                <Td className="text-right">
                  <Button variant="secondary" onClick={() => save(r.key_name)} disabled={!(r.key_name in edited)}><Save size={14}/> Simpan</Button>
                </Td>
              </tr>
            ))}
            {issuerKeys.length === 0 && <tr><Td colSpan={4} className="text-center text-slate-500 py-6">Belum ada setting per issuer.</Td></tr>}
          </tbody>
        </Table>
      </Card>

      {/* ─── Tutorial PDF download ─── */}
      <Card className="mt-5">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <h3 className="font-bold inline-flex items-center gap-2">📘 Tutorial Pengguna</h3>
          <span className="text-[11px] text-slate-500">Panduan lengkap step-by-step dengan screenshot</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-4">
          {[
            { role: "operation", label: "Operation", color: "bg-blue-50 text-blue-800 border-blue-200", ext: "pdf" },
            { role: "finance", label: "Finance", color: "bg-emerald-50 text-emerald-800 border-emerald-200", ext: "pdf" },
            { role: "administrator", label: "Administrator", color: "bg-slate-50 text-slate-800 border-slate-200", ext: "pdf" },
            { role: "sales-pitch", label: "Sales Presentation", color: "bg-amber-50 text-amber-800 border-amber-200", ext: "pptx" },
          ].map((r) => (
            <button
              key={r.role}
              data-testid={`tutorial-dl-${r.role}`}
              onClick={async () => {
                try {
                  const resp = await api.get(`/tutorials/${r.role}`, { responseType: "blob" });
                  const filename = r.role === "sales-pitch" ? "arc_sales_presentation.pptx" : `tutorial_${r.role}.pdf`;
                  const blob = new Blob([resp.data], {
                    type: r.ext === "pptx"
                      ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      : "application/pdf",
                  });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = filename;
                  a.rel = "noopener";
                  document.body.appendChild(a);
                  a.click();
                  // Cleanup after browser starts the download
                  setTimeout(() => {
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                  }, 1500);
                } catch (e) {
                  console.error("download error", e);
                  alert("Gagal unduh: " + (e?.response?.data?.detail || e.message));
                }
              }}
              className={`border rounded-lg p-4 text-left transition hover:shadow-md ${r.color}`}
            >
              <div className="text-xs uppercase font-bold tracking-wider opacity-70">
                {r.ext.toUpperCase()} {r.role === "sales-pitch" ? "Sales Deck" : "Tutorial"}
              </div>
              <div className="font-heading font-black text-lg mt-1">{r.label}</div>
              <div className="text-xs opacity-70 mt-1">Klik untuk unduh →</div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
